import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CommercialEntityForm from '../../crm/Leads/CommercialEntityForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import FilterBanner from '../../../shared/FilterBanner';
import CancelButton from '../../../shared/CancelButton';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { PlaceholderThumb, PersonPlaceholderThumb } from '../../../shared/ProductThumb';
import { CategoryPill, ActiveStatusPill } from '../../../shared/Pill';
import { formatAddress } from '../../../shared/formatAddress';
import { useClassificationOptions, ClassificationFilterFields, PrimaryClassificationCell } from '../../../shared/OrganisationClassification';
import { Filter as FilterIcon, ChevronDown, ChevronRight } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const TYPE_LABELS = { organization: 'Organisation', individual: 'Individual' };
// Identifies this screen's saved filters (SavedFilterTabs) from any other
// screen's — same shared saved_filters table/pattern as Products/Customers.
const FILTER_SCREEN_KEY = 'all-entities';
const DEFAULT_FILTERS = { status: 'all', relationship_type_id: '', type: '', market_id: '', market_segment_id: '', operator_id: '', channel_id: '', tier_id: '', legal_entity_type_id: '', owner: '', country: '', sync: '' };
const OPTIONAL_COLUMNS = [
  { key: 'type', label: 'Shape', group: 'Details' },
  { key: 'legal_entity', label: 'Legal Entity', group: 'Details' },
  { key: 'market', label: 'Market', group: 'Classification' },
  { key: 'segment', label: 'Segment', group: 'Classification' },
  { key: 'operator', label: 'Operator', group: 'Classification' },
  { key: 'channel', label: 'Outlet', group: 'Classification' },
  { key: 'tier', label: 'Tier', group: 'Classification' },
  { key: 'address', label: 'Address', group: 'Location & Contact' },
  { key: 'state', label: 'State', group: 'Location & Contact' },
  { key: 'country', label: 'Country', group: 'Location & Contact' },
  { key: 'phone', label: 'Phone', group: 'Location & Contact' },
  { key: 'owner', label: 'Account Owner', group: 'Admin' },
  { key: 'sync', label: 'Email Sync', group: 'Admin' },
  { key: 'status', label: 'Status', group: 'Admin' },
];
// Always shown — they identify the row and its relationship (the "+N"
// expander rides along with Relationship), so they're not in the picker.
const SYSTEM_COLUMNS = ['Logo/Photo', 'Name', 'Relationship', 'Stage', 'Actions'];
// The everyday job (ui-architecture.md Column Picker): who it is, what it
// is to us (the system columns), where, and how to reach them. Market is
// left off — it's blank for Contacts and most Suppliers. Everything else is
// one tick away in Columns.
const DEFAULT_VISIBLE_COLUMNS = ['state', 'phone', 'status'];
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// The one relationship the Relationship/Stage columns show for this row —
// an entity holding several Relationship Types at once (Customer AND
// Supplier AND...) can't show all of them in two plain columns, so this
// picks exactly one, same "one row, one meaning" as every other list
// screen's columns:
//   - No Relationship Type filter active: the entity's own designated
//     Principal (commercialEntityRelationship.js's is_principal) — what the
//     record is primarily identified as. Falls back to whichever
//     relationship happens to be first if somehow none is flagged
//     Principal (shouldn't happen — every entity always has exactly one —
//     but a row must never render blank over a data hiccup).
//   - A Relationship Type filter active: that entity's relationship for the
//     filtered Type specifically, regardless of which one is Principal —
//     since the whole list is already narrowed to "holds this Type," the
//     columns answer "what's its status in the thing I filtered by," not
//     "what's it primarily known as."
function displayedRelationship(item, relationshipTypeIdFilter) {
  const rels = item.relationships || [];
  if (relationshipTypeIdFilter) {
    const matched = rels.find((r) => String(r.relationship_type_id) === String(relationshipTypeIdFilter));
    if (matched) return matched;
  }
  return rels.find((r) => r.is_principal) || rels[0] || null;
}

// Every relationship this record holds *besides* the one already shown in
// the Relationship/Stage columns — the +N expand's contents. Not a fixed
// "every non-Principal one": when a Relationship Type filter has swapped
// the displayed relationship to something other than the Principal, the
// Principal itself reappears here instead.
function otherRelationships(item, displayed) {
  return (item.relationships || []).filter((r) => r.id !== displayed?.id);
}

// Market Intel's "All Entities" list — every Commercial Entity for this
// tenant regardless of Relationship Type (Customer, Supplier, Manufacturer,
// whatever RelationshipTypeTable holds), unlike the Prospects & Customers screen
// which is scoped to the Prospect and Customer Relationship Types
// (see backend's listAllCommercialEntities vs listCommercialEntities). Row
// click / View both go to the same dedicated detail page those screens use
// (CommercialEntityDetailPage.js) — this is a different filtered view over
// the same underlying records, not a separate record type.
function AllEntitiesTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // Relationship Type seeds from the URL on first load — Relationship
  // Types' own "In Use" column (RelationshipTypeTable.js) drills through to
  // this same filter, since it's already a first-class FilterDrawer field
  // here (unlike Link Role's own drill-through below, which has no drawer
  // field of its own to seed). Market, Market Segment, Operator and Channel
  // seed the same way from the Markets, Operators and Channels screens' "In
  // Use" columns.
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    relationship_type_id: searchParams.get('relationship_type_id') || DEFAULT_FILTERS.relationship_type_id,
    market_id: searchParams.get('market_id') || DEFAULT_FILTERS.market_id,
    market_segment_id: searchParams.get('market_segment_id') || DEFAULT_FILTERS.market_segment_id,
    operator_id: searchParams.get('operator_id') || DEFAULT_FILTERS.operator_id,
    channel_id: searchParams.get('channel_id') || DEFAULT_FILTERS.channel_id,
  }));
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showType = visibleColumns.includes('type');
  const showMarket = visibleColumns.includes('market');
  const showSegment = visibleColumns.includes('segment');
  const showOperator = visibleColumns.includes('operator');
  const showChannel = visibleColumns.includes('channel');
  const showTier = visibleColumns.includes('tier');
  const showLegalEntity = visibleColumns.includes('legal_entity');
  const showOwner = visibleColumns.includes('owner');
  const showState = visibleColumns.includes('state');
  const showAddress = visibleColumns.includes('address');
  const showPhone = visibleColumns.includes('phone');
  const showSync = visibleColumns.includes('sync');
  const showCountry = visibleColumns.includes('country');
  const showStatus = visibleColumns.includes('status');
  const totalCols = 6 // media, Name, Relationship, Stage, "+N" expander, actions
    + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length;
  const { status, relationship_type_id: relationshipTypeId, type, market_id: marketId, market_segment_id: marketSegmentId, operator_id: operatorId, channel_id: channelId, tier_id: tierId, legal_entity_type_id: legalEntityTypeId, owner: ownerFilter, country, sync: syncFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Free-text search, kept outside DEFAULT_FILTERS/SavedFilterTabs — same
  // split ProductTable.js uses (search is unbounded free text, not a saved
  // filter value). Matches name, first name, or last name (backend's
  // listAllCommercialEntities), so it finds a Prospect/Customer/Supplier/etc.
  // by company name or by an Individual's own name.
  const [search, setSearch] = useState('');
  const [countryOptions, setCountryOptions] = useState([]);
  const [relationshipTypeOptions, setRelationshipTypeOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [legalEntityTypeOptions, setLegalEntityTypeOptions] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const { markets: marketOptions, segments: segmentOptions, channels: channelOptions, operators: operatorOptions } = useClassificationOptions();
  const [loading, setLoading] = useState(false);
  // +N accordion — one row expanded at a time, same shape as
  // ErrorCatalogPage.js/FieldHelpTable.js's own expandedId.
  const [expandedId, setExpandedId] = useState(null);

  // Relationship column sort — client-side, over whichever page is
  // currently loaded: the displayed relationship isn't a plain backend
  // column (it's derived per-row per the comment on displayedRelationship
  // above), unlike every other sortable column in the app which sorts via
  // the backend's own sortBy/sortOrder (see ProductTable.js).
  const [relSortOrder, setRelSortOrder] = useState(null); // null | 'ASC' | 'DESC'
  const toggleRelSort = () => setRelSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
  const displayRows = useMemo(() => {
    if (!relSortOrder) return rows;
    const key = (item) => displayedRelationship(item, relationshipTypeId)?.relationship_type?.name || '';
    const sorted = [...rows].sort((a, b) => key(a).localeCompare(key(b)));
    return relSortOrder === 'DESC' ? sorted.reverse() : sorted;
  }, [rows, relSortOrder, relationshipTypeId]);

  // Ad hoc "Filters" panel — draft is null while closed, same pattern as
  // ProductTable.js's Filters button. Shares renderDrawerFields with
  // SavedFilterTabs below so the two never drift apart.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All items</option>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Relationship Type</label><br />
        <select className="styled-input" value={draft.relationship_type_id} onChange={(e) => setDraftField('relationship_type_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All types</option>
          {relationshipTypeOptions.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Shape</label><br />
        <select className="styled-input" value={draft.type} onChange={(e) => setDraftField('type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All types</option>
          <option value="organization">Organisation</option>
          <option value="individual">Individual</option>
        </select>
      </div>
      <ClassificationFilterFields draft={draft} setDraftField={setDraftField} markets={marketOptions} segments={segmentOptions} channels={channelOptions} operators={operatorOptions} labelStyle={FIELD_LABEL_STYLE} inputStyle={FIELD_INPUT_STYLE} />
      <div>
        <label style={FIELD_LABEL_STYLE}>Tier</label><br />
        <select className="styled-input" value={draft.tier_id} onChange={(e) => setDraftField('tier_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All tiers</option>
          {tierOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Legal Entity</label><br />
        <select className="styled-input" value={draft.legal_entity_type_id} onChange={(e) => setDraftField('legal_entity_type_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All legal entities</option>
          {legalEntityTypeOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Account Owner</label><br />
        <select className="styled-input" value={draft.owner} onChange={(e) => setDraftField('owner', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">Anyone</option>
          <option value="me">Me</option>
          <option value="none">No owner</option>
          {ownerOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Country</label><br />
        <select className="styled-input" value={draft.country} onChange={(e) => setDraftField('country', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All countries</option>
          {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Email Sync</label><br />
        <select className="styled-input" value={draft.sync} onChange={(e) => setDraftField('sync', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          <option value="synced">On</option>
          <option value="not_synced">Off</option>
        </select>
      </div>
    </>
  );

  // Drill-through from the Market Intel Dashboard's stale-count cards
  // (MarketIntelDashboard.js) — same "no prior filter support" shape as
  // CommercialEntityTable.js's own stale drill-through, just against the tenant-wide
  // All Entities endpoint instead of Customers only.
  const staleFilter = searchParams.get('opening_hours_stale') === 'true';
  const clearStaleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('opening_hours_stale');
    setSearchParams(next);
    setPage(1);
  };

  const addressStaleFilter = searchParams.get('address_stale') === 'true';
  const clearAddressStaleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('address_stale');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Relationship Types' own "In Use" column
  // (RelationshipTypeTable.js) — relationship_type_name only ever arrives
  // alongside relationship_type_id from that link, so its presence (rather
  // than filters.relationship_type_id, which the drawer's own select sets
  // too) is what distinguishes "arrived via drill-through" for the banner.
  const relationshipTypeNameFilter = searchParams.get('relationship_type_name');
  const clearRelationshipTypeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('relationship_type_id');
    next.delete('relationship_type_name');
    setSearchParams(next);
    applyFilters({ ...filters, relationship_type_id: '' });
  };

  // Drill-through from the Markets screen's Market and Segment "In Use"
  // columns (MarketTable.js) — seeds the Market / Segment
  // filters above, same as relationship_type_id; the name only arrives with
  // the link, so its presence is what shows the banner.
  const marketNameFilter = searchParams.get('market_name');
  const segmentNameFilter = searchParams.get('market_segment_name');
  const clearMarketSegmentFilter = () => {
    const next = new URLSearchParams(searchParams);
    ['market_id', 'market_name', 'market_segment_id', 'market_segment_name'].forEach((k) => next.delete(k));
    setSearchParams(next);
    applyFilters({ ...filters, market_id: '', market_segment_id: '' });
  };
  const operatorNameFilter = searchParams.get('operator_name');
  const clearOperatorFilter = () => {
    const next = new URLSearchParams(searchParams);
    ['operator_id', 'operator_name'].forEach((k) => next.delete(k));
    setSearchParams(next);
    applyFilters({ ...filters, operator_id: '' });
  };
  const channelNameFilter = searchParams.get('channel_name');
  const clearChannelFilter = () => {
    const next = new URLSearchParams(searchParams);
    ['channel_id', 'channel_name'].forEach((k) => next.delete(k));
    setSearchParams(next);
    applyFilters({ ...filters, channel_id: '' });
  };

  // Drill-through from Link Role Types' own "In Use" column
  // (LinkRoleTypeTable.js) — same shape as the stale filters above (a plain
  // URL param read directly, no FilterDrawer field of its own), just
  // carrying an id + name pair like tier_id/tier_name (CommercialEntityTable.js)
  // rather than a boolean.
  const linkRoleTypeIdFilter = searchParams.get('link_role_type_id');
  const linkRoleTypeNameFilter = searchParams.get('link_role_type_name');
  const clearLinkRoleTypeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('link_role_type_id');
    next.delete('link_role_type_name');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Email Types' own "In Use" column (EmailTypeTable.js)
  // — same id+name pair shape as link_role_type_id above, just one-sided.
  const emailTypeIdFilter = searchParams.get('email_type_id');
  const emailTypeNameFilter = searchParams.get('email_type_name');
  const clearEmailTypeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('email_type_id');
    next.delete('email_type_name');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Legal Entity Types' own "In Use" column
  // (LegalEntityTypeTable.js) — same shape as email_type_id above.
  const legalEntityTypeIdFilter = searchParams.get('legal_entity_type_id');
  const legalEntityTypeNameFilter = searchParams.get('legal_entity_type_name');
  const clearLegalEntityTypeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('legal_entity_type_id');
    next.delete('legal_entity_type_name');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Phone Types' own "In Use" column (PhoneTypeTable.js)
  // — same shape as email_type_id above.
  const phoneTypeIdFilter = searchParams.get('phone_type_id');
  const phoneTypeNameFilter = searchParams.get('phone_type_name');
  const clearPhoneTypeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('phone_type_id');
    next.delete('phone_type_name');
    setSearchParams(next);
    setPage(1);
  };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/commercial-entities/countries', { params: { scope: 'all' } })
      .then((res) => setCountryOptions(res.data.data || []))
      .catch(() => setCountryOptions([]));
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => setRelationshipTypeOptions(res.data.data || []))
      .catch(() => setRelationshipTypeOptions([]));
    axios.get('/api/customer-tiers', { params: { status: 'active' } })
      .then((res) => setTierOptions(res.data.data || []))
      .catch(() => setTierOptions([]));
    axios.get('/api/legal-entity-types', { params: { status: 'active' } })
      .then((res) => setLegalEntityTypeOptions(res.data.data || []))
      .catch(() => setLegalEntityTypeOptions([]));
    axios.get('/api/users/account-owners')
      .then((res) => setOwnerOptions(res.data.data || []))
      .catch(() => setOwnerOptions([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, status, country, relationshipTypeId, type, marketId, marketSegmentId, operatorId, channelId, tierId, legalEntityTypeId, ownerFilter, syncFilter, page, limit, staleFilter, addressStaleFilter, linkRoleTypeIdFilter, emailTypeIdFilter, phoneTypeIdFilter, legalEntityTypeIdFilter]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { status, page, limit };
      if (search) params.search = search;
      if (country) params.country = country;
      if (relationshipTypeId) params.relationship_type_id = relationshipTypeId;
      if (type) params.type = type;
      if (marketId) params.market_id = marketId;
      if (marketSegmentId) params.market_segment_id = marketSegmentId;
      if (operatorId) params.operator_id = operatorId;
      if (channelId) params.channel_id = channelId;
      if (tierId) params.tier_id = tierId;
      if (ownerFilter) params.owner = ownerFilter;
      if (syncFilter) params.sync = syncFilter;
      if (linkRoleTypeIdFilter) params.link_role_type_id = linkRoleTypeIdFilter;
      if (emailTypeIdFilter) params.email_type_id = emailTypeIdFilter;
      if (phoneTypeIdFilter) params.phone_type_id = phoneTypeIdFilter;
      if (legalEntityTypeId || legalEntityTypeIdFilter) params.legal_entity_type_id = legalEntityTypeId || legalEntityTypeIdFilter;
      if (staleFilter) params.opening_hours_stale = 'true';
      if (addressStaleFilter) params.address_stale = 'true';
      const res = await axios.get('/api/commercial-entities/all', { params });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setShowForm(true);
  };

  const handleView = (item) => {
    navigate(`/commercial-entities/${item.id}`, { state: { browseIds: displayRows.map((r) => r.id), origin: { label: 'All Entities', path: '/market-intel-all-entities' } } });
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/commercial-entities/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/commercial-entities/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/commercial-entities/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      await axios.post('/api/commercial-entities', data);
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>All Entities</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Entity
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 200 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the entity list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {relationshipTypeNameFilter && (
        <FilterBanner
          message={`Showing entities holding the "${relationshipTypeNameFilter}" Relationship Type`}
          onClear={clearRelationshipTypeFilter}
        />
      )}

      {(marketNameFilter || segmentNameFilter) && (
        <FilterBanner
          message={segmentNameFilter
            ? `Showing Organisations in Market Segment "${segmentNameFilter}"`
            : `Showing Organisations in Market "${marketNameFilter}"`}
          onClear={clearMarketSegmentFilter}
        />
      )}

      {operatorNameFilter && (
        <FilterBanner message={`Showing Organisations with Operator "${operatorNameFilter}"`} onClear={clearOperatorFilter} />
      )}

      {channelNameFilter && (
        <FilterBanner message={`Showing Organisations in Outlet "${channelNameFilter}"`} onClear={clearChannelFilter} />
      )}

      {linkRoleTypeIdFilter && (
        <FilterBanner
          message={`Showing entities linked via the "${linkRoleTypeNameFilter || linkRoleTypeIdFilter}" Link Role`}
          onClear={clearLinkRoleTypeFilter}
        />
      )}

      {legalEntityTypeIdFilter && (
        <FilterBanner
          message={`Showing entities with Legal Entity "${legalEntityTypeNameFilter || legalEntityTypeIdFilter}"`}
          onClear={clearLegalEntityTypeFilter}
        />
      )}

      {emailTypeIdFilter && (
        <FilterBanner
          message={`Showing entities with an email of Type "${emailTypeNameFilter || emailTypeIdFilter}"`}
          onClear={clearEmailTypeFilter}
        />
      )}

      {phoneTypeIdFilter && (
        <FilterBanner
          message={`Showing entities with a phone of Type "${phoneTypeNameFilter || phoneTypeIdFilter}"`}
          onClear={clearPhoneTypeFilter}
        />
      )}

      {staleFilter && (
        <FilterBanner message="Showing entities with stale Opening Hours" onClear={clearStaleFilter} />
      )}

      {addressStaleFilter && (
        <FilterBanner message="Showing entities with a stale Address" onClear={clearAddressStaleFilter} />
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <CommercialEntityForm
            initial={null}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
            creationContext="free"
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            <col />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '6%' }} />
            {showType && <col style={{ width: '9%' }} />}
            {showLegalEntity && <col style={{ width: '9%' }} />}
            {showMarket && <col style={{ width: '10%' }} />}
            {showSegment && <col style={{ width: '12%' }} />}
            {showOperator && <col style={{ width: '10%' }} />}
            {showChannel && <col style={{ width: '10%' }} />}
            {showTier && <col style={{ width: '9%' }} />}
            {showAddress && <col style={{ width: '12%' }} />}
            {showState && <col style={{ width: '9%' }} />}
            {showCountry && <col style={{ width: '10%' }} />}
            {showPhone && <col style={{ width: '14%' }} />}
            {showOwner && <col style={{ width: '9%' }} />}
            {showSync && <col style={{ width: '9%' }} />}
            {showStatus && <col style={{ width: '10%' }} />}
            <col style={{ width: 56 }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={toggleRelSort} title="Sort by Relationship">
                Relationship {relSortOrder ? (relSortOrder === 'ASC' ? '▲' : '▼') : ''}
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Stage</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
              {showType && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Shape</th>}
              {showLegalEntity && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Legal Entity</th>}
              {showMarket && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Market</th>}
              {showSegment && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Segment</th>}
              {showOperator && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Operator</th>}
              {showChannel && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Outlet</th>}
              {showTier && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Tier</th>}
              {showAddress && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Address</th>}
              {showState && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>State</th>}
              {showCountry && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Country</th>}
              {showPhone && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Phone</th>}
              {showOwner && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Account Owner</th>}
              {showSync && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Email Sync</th>}
              {showStatus && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : displayRows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : displayRows.map((item) => {
              const displayed = displayedRelationship(item, relationshipTypeId);
              const others = otherRelationships(item, displayed);
              const isExpanded = expandedId === item.id;
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className="list-row"
                    onClick={() => { if (!showForm) handleView(item); }}
                    style={{ cursor: showForm ? 'default' : 'pointer' }}
                    title={showForm ? undefined : 'Click to view'}
                  >
                    <td className="cell-media" style={{ padding: '6px 8px' }}>
                      {item.logo ? (
                        <img src={thumbFileUrl(item.logo.id)} alt="" style={{ display: 'block', margin: '0 auto', width: 42, height: 42, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, padding: 2 }} />
                      ) : item.type === 'individual' ? (
                        <PersonPlaceholderThumb size={42} />
                      ) : (
                        <PlaceholderThumb size={42} />
                      )}
                    </td>
                    <td className="cell-title" style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                    <td data-label="Relationship" style={{ padding: '6px 8px', fontSize: 14 }}>
                      {displayed?.relationship_type?.name ? <CategoryPill value={displayed.relationship_type.name} /> : '—'}
                    </td>
                    <td data-label="Stage" style={{ padding: '6px 8px', fontSize: 14 }}>
                      {displayed?.stage?.name ? <CategoryPill value={displayed.stage.name} /> : '—'}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {others.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          title={isExpanded ? 'Hide other relationships' : 'Show other relationships'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer', color: '#5a6b85', fontSize: 12, fontWeight: 600, padding: '2px 4px' }}
                        >
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          +{others.length}
                        </button>
                      )}
                    </td>
                    {showType && <td data-label="Shape" style={{ padding: '6px 8px', fontSize: 14 }}><CategoryPill value={TYPE_LABELS[item.type] || item.type} /></td>}
                    {showLegalEntity && <td data-label="Legal Entity" style={{ padding: '6px 8px', fontSize: 14 }}>{item.legal_entity_type?.name || '—'}</td>}
                    {showMarket && <td data-label="Market" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="market" /></td>}
                    {showSegment && <td data-label="Segment" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="segment" /></td>}
                    {showOperator && <td data-label="Operator" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="operator" /></td>}
                    {showChannel && <td data-label="Outlet" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="channel" /></td>}
                    {showTier && <td data-label="Tier" style={{ padding: '6px 8px', fontSize: 14 }}>{item.tier?.name || '—'}</td>}
                    {showAddress && <td data-label="Address" style={{ padding: '6px 8px', fontSize: 14 }}>{formatAddress(item.primary_address) || '—'}</td>}
                    {showState && <td data-label="State" style={{ padding: '6px 8px', fontSize: 14 }}>{item.primary_address?.state || '—'}</td>}
                    {showCountry && (
                      <td data-label="Country" style={{ padding: '6px 8px', fontSize: 14 }}>
                        {item.primary_address?.country || '—'}
                      </td>
                    )}
                    {showPhone && <td data-label="Phone" style={{ padding: '6px 8px', fontSize: 14 }}>{item.phones?.length ? item.phones.map((p) => p.phone_number).join(', ') : '—'}</td>}
                    {showOwner && <td data-label="Account Owner" style={{ padding: '6px 8px', fontSize: 14 }}>{item.account_owner ? `${item.account_owner.name}${item.account_owner.is_active ? '' : ' (switched off)'}` : '—'}</td>}
                    {showSync && (
                      <td data-label="Email Sync" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                        <ActiveStatusPill active={item.sync} activeLabel="On" inactiveLabel="Off" />
                      </td>
                    )}
                    {showStatus && (
                      <td data-label="Status" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                        <ActiveStatusPill active={item.is_active} />
                      </td>
                    )}
                    <td className="cell-actions" style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={[
                          { key: 'view', label: 'View', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleView(item) },
                          ...(item.is_active
                            ? [{ key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }]
                            : [
                                { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                                { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
                              ]),
                        ]}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={totalCols} style={{ padding: '10px 16px 14px', background: '#f8fafc', borderBottom: '1px solid #e3e7ee' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {others.map((r) => (
                            <CategoryPill key={r.id} value={r.relationship_type?.name} title={r.stage?.name ? `${r.relationship_type.name}: ${r.stage.name}` : r.relationship_type?.name} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Entities"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default AllEntitiesTable;
